/**
 * errorLog — module-level singleton error store.
 *
 * All toast.error() calls across the app are routed here instead of showing
 * a popup.  React components subscribe via useErrorLog() to get live updates.
 * No React context is needed; the store lives outside the component tree.
 */

import React from 'react'

const MAX_ENTRIES = 200

let _entries   = []   // [{ id, message, source, timestamp }]
let _listeners = []   // Array of () => void

const _notify = () => _listeners.slice().forEach(fn => fn())

/** Add a new error entry to the log. */
export const addErrorLog = (message, source = '') => {
  _entries = [
    {
      id:        Date.now() + Math.random(),
      message:   String(message),
      source,
      timestamp: new Date(),
    },
    ..._entries,
  ].slice(0, MAX_ENTRIES)
  _notify()
}

/** Return a copy of all entries (newest first). */
export const getErrorLog = () => [..._entries]

/** Wipe the log. */
export const clearErrorLog = () => {
  _entries = []
  _notify()
}

/** Subscribe to log changes. Returns an unsubscribe function. */
export const subscribeErrorLog = (fn) => {
  _listeners.push(fn)
  return () => { _listeners = _listeners.filter(l => l !== fn) }
}

/**
 * React hook — returns live entries array.
 * Re-renders the consuming component whenever a new error is logged.
 */
export const useErrorLog = () => {
  const [entries, setEntries] = React.useState(() => [..._entries])
  React.useEffect(() => {
    // Sync immediately in case entries were added before this component mounted
    setEntries([..._entries])
    return subscribeErrorLog(() => setEntries([..._entries]))
  }, [])
  return entries
}
